import React from "react";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import BrandTextLink from "../BrandTextLink/BrandTextLink";
import { grabbingIcon as GrabbingIcon } from "../../assets/icons";
import Button from "../Buttons/Button";
import ChoiceControl from "../ChoiceControl/ChoiceControl";
import DragAndDropField from "../DragAndDropField/DragAndDropField";
import Select from "../Select";
import Switch from "../Switch/Switch";
import type {
  AssignmentTestDefinition,
  AssignmentTestDraft,
  AssignmentTestOption,
  AssignmentTestQuestion,
  AssignmentTestQuestionType,
} from "../../services/assignmentService/assignmentService.types";
import { importAssignmentTestFromWord } from "../../services/assignmentService/assignmentService";
import {
  AddQuestionRow,
  AttachmentArea,
  BuilderHead,
  BuilderHint,
  BuilderSection,
  BuilderTitle,
  DragHandle,
  ExistingAttachment,
  FieldLabel,
  FieldsStack,
  OptionInput,
  OptionRow,
  OptionsHead,
  OptionsSection,
  OptionsTitle,
  QuestionActions,
  QuestionBlock,
  QuestionNumber,
  QuestionsList,
  QuestionTop,
  TextAction,
  TextArea,
  ValidationMessage,
  WordFileButton,
  WordImportBox,
  HiddenFileInput,
} from "./AssignmentTestBuilder.styles";

let draftSequence = 0;
const nextClientId = (prefix: string) => `${prefix}-${++draftSequence}`;
const sortableId = (item: { client_id?: string; id?: number }) => item.client_id || String(item.id);

const createOption = (overrides: Partial<AssignmentTestOption> = {}): AssignmentTestOption => ({
  client_id: nextClientId("option"),
  text: "",
  is_correct: false,
  ...overrides,
});

const createQuestion = (overrides: Partial<AssignmentTestQuestion> = {}): AssignmentTestQuestion => ({
  client_id: nextClientId("question"),
  question_type: "single_choice",
  text: "",
  comment: "",
  options: [createOption({ is_correct: true }), createOption()],
  ...overrides,
});

export const createAssignmentTestDraft = (
  definition: AssignmentTestDefinition | null,
): AssignmentTestDraft | null => {
  if (!definition) return null;
  return {
    questions: definition.questions.map((question) => ({
      ...question,
      client_id: nextClientId("question"),
      attachment_file: null,
      remove_attachment: false,
      options: question.options.map((option) => ({
        ...option,
        client_id: nextClientId("option"),
      })),
    })),
  };
};

export const reorderAssignmentTestQuestions = (
  questions: AssignmentTestQuestion[],
  activeId: string | number,
  overId: string | number,
): AssignmentTestQuestion[] => {
  const oldIndex = questions.findIndex((question) => sortableId(question) === String(activeId));
  const newIndex = questions.findIndex((question) => sortableId(question) === String(overId));
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return questions;
  return arrayMove(questions, oldIndex, newIndex);
};

export const reorderAssignmentTestOptions = (
  options: AssignmentTestOption[],
  activeId: string | number,
  overId: string | number,
): AssignmentTestOption[] => {
  const oldIndex = options.findIndex((option) => sortableId(option) === String(activeId));
  const newIndex = options.findIndex((option) => sortableId(option) === String(overId));
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return options;
  return arrayMove(options, oldIndex, newIndex);
};

export const validateAssignmentTestDraft = (draft: AssignmentTestDraft | null): string[] => {
  if (!draft) return [];
  if (draft.questions.length === 0) return ["Добавьте хотя бы один вопрос или отключите тест."];
  const errors: string[] = [];
  draft.questions.forEach((question, index) => {
    if (!question.text.trim()) errors.push(`Вопрос ${index + 1}: заполните текст.`);
    if (question.options.length < 2) errors.push(`Вопрос ${index + 1}: добавьте минимум два варианта.`);
    question.options.forEach((option, optionIndex) => {
      if (!option.text.trim()) errors.push(`Вопрос ${index + 1}, вариант ${optionIndex + 1}: заполните текст.`);
    });
    const correctCount = question.options.filter((option) => option.is_correct).length;
    if (question.question_type === "single_choice" && correctCount !== 1) {
      errors.push(`Вопрос ${index + 1}: отметьте ровно один правильный ответ.`);
    }
    if (question.question_type === "multiple_choice" && correctCount < 1) {
      errors.push(`Вопрос ${index + 1}: отметьте хотя бы один правильный ответ.`);
    }
  });
  return errors;
};

interface AssignmentTestBuilderProps {
  value: AssignmentTestDraft | null;
  onChange: (value: AssignmentTestDraft | null) => void;
}

interface SortableOptionProps {
  option: AssignmentTestOption;
  question: AssignmentTestQuestion;
  onChange: (option: AssignmentTestOption) => void;
  onRemove: () => void;
  onCorrectChange: () => void;
}

const SortableOption: React.FC<SortableOptionProps> = ({
  option,
  question,
  onChange,
  onRemove,
  onCorrectChange,
}) => {
  const sortable = useSortable({ id: sortableId(option) });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  return (
    <OptionRow ref={sortable.setNodeRef} style={style} $dragging={sortable.isDragging}>
      <DragHandle
        type="button"
        aria-label="Перетащить вариант"
        {...sortable.attributes}
        {...sortable.listeners}
      >
        <GrabbingIcon aria-hidden="true" />
      </DragHandle>
      <ChoiceControl
        type={question.question_type === "single_choice" ? "radio" : "checkbox"}
        name={`correct-${question.client_id || question.id}`}
        checked={option.is_correct}
        onChange={onCorrectChange}
        aria-label="Правильный вариант"
      />
      <OptionInput
        value={option.text}
        onChange={(event) => onChange({ ...option, text: event.target.value })}
        placeholder="Вариант ответа"
      />
      <TextAction type="button" $danger onClick={onRemove} disabled={question.options.length <= 2}>
        Удалить
      </TextAction>
    </OptionRow>
  );
};

interface SortableQuestionProps {
  question: AssignmentTestQuestion;
  index: number;
  onChange: (question: AssignmentTestQuestion) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

const SortableQuestion: React.FC<SortableQuestionProps> = ({
  question,
  index,
  onChange,
  onDuplicate,
  onRemove,
}) => {
  const sortable = useSortable({ id: sortableId(question) });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  const updateOption = (optionIndex: number, option: AssignmentTestOption) => {
    const options = question.options.map((item, currentIndex) => currentIndex === optionIndex ? option : item);
    onChange({ ...question, options });
  };

  const toggleCorrect = (optionIndex: number) => {
    const options = question.options.map((option, currentIndex) => {
      if (question.question_type === "single_choice") {
        return { ...option, is_correct: currentIndex === optionIndex };
      }
      return currentIndex === optionIndex ? { ...option, is_correct: !option.is_correct } : option;
    });
    onChange({ ...question, options });
  };

  const changeType = (value: AssignmentTestQuestionType) => {
    let options = question.options;
    if (value === "single_choice") {
      const firstCorrect = Math.max(0, options.findIndex((option) => option.is_correct));
      options = options.map((option, optionIndex) => ({ ...option, is_correct: optionIndex === firstCorrect }));
    }
    onChange({ ...question, question_type: value, options });
  };

  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const options = reorderAssignmentTestOptions(question.options, active.id, over.id);
    if (options !== question.options) onChange({ ...question, options });
  };

  const selectedFiles = question.attachment_file ? [question.attachment_file] : [];

  return (
    <QuestionBlock ref={sortable.setNodeRef} style={style} $dragging={sortable.isDragging}>
      <QuestionTop>
        <DragHandle
          type="button"
          aria-label="Перетащить вопрос"
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <GrabbingIcon aria-hidden="true" />
        </DragHandle>
        <QuestionNumber>Вопрос {index + 1}</QuestionNumber>
        <Select
          options={[
            { label: "Один вариант", value: "single_choice" },
            { label: "Несколько вариантов", value: "multiple_choice" },
          ]}
          value={question.question_type}
          onChange={(value) => changeType(value as AssignmentTestQuestionType)}
          ariaLabel={`Тип вопроса ${index + 1}`}
          fullWidth
        />
        <QuestionActions>
          <TextAction type="button" onClick={onDuplicate}>Дублировать</TextAction>
          <TextAction type="button" $danger onClick={onRemove}>Удалить</TextAction>
        </QuestionActions>
      </QuestionTop>

      <FieldsStack>
        <FieldLabel>
          Вопрос
          <TextArea
            value={question.text}
            onChange={(event) => onChange({ ...question, text: event.target.value })}
            placeholder="Сформулируйте вопрос"
          />
        </FieldLabel>
        <FieldLabel>
          Комментарий к вопросу
          <TextArea
            value={question.comment}
            onChange={(event) => onChange({ ...question, comment: event.target.value })}
            placeholder="Необязательно: пояснение или контекст"
          />
        </FieldLabel>
      </FieldsStack>

      <OptionsSection>
        <OptionsHead>
          <OptionsTitle>
            Варианты ответа · {question.question_type === "single_choice" ? "один правильный" : "один или несколько правильных"}
          </OptionsTitle>
          <TextAction
            type="button"
            onClick={() => onChange({ ...question, options: [...question.options, createOption()] })}
          >
            Добавить вариант
          </TextAction>
        </OptionsHead>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOptionDragEnd}>
          <SortableContext
            items={question.options.map((option) => sortableId(option))}
            strategy={verticalListSortingStrategy}
          >
            {question.options.map((option, optionIndex) => (
              <SortableOption
                key={sortableId(option)}
                option={option}
                question={question}
                onChange={(nextOption) => updateOption(optionIndex, nextOption)}
                onCorrectChange={() => toggleCorrect(optionIndex)}
                onRemove={() => onChange({
                  ...question,
                  options: question.options.filter((_, currentIndex) => currentIndex !== optionIndex),
                })}
              />
            ))}
          </SortableContext>
        </DndContext>
      </OptionsSection>

      <AttachmentArea>
        <OptionsTitle>Вложение к вопросу</OptionsTitle>
        {question.attachment && !question.remove_attachment && !question.attachment_file ? (
          <ExistingAttachment>
            <BrandTextLink href={question.attachment.url} target="_blank" rel="noreferrer">{question.attachment.name}</BrandTextLink>
            <TextAction
              type="button"
              $danger
              onClick={() => onChange({ ...question, remove_attachment: true })}
            >
              Удалить вложение
            </TextAction>
          </ExistingAttachment>
        ) : null}
        <DragAndDropField
          variant="compact"
          placeholder="Добавить файл к вопросу"
          infoText="Фото, GIF или документ · один файл"
          files={selectedFiles}
          maxFiles={1}
          onFileSelected={(files) => onChange({
            ...question,
            attachment_file: files?.[0] || null,
            remove_attachment: Boolean(files?.[0]) ? false : question.remove_attachment,
          })}
        />
      </AttachmentArea>
    </QuestionBlock>
  );
};

const AssignmentTestBuilder: React.FC<AssignmentTestBuilderProps> = ({ value, onChange }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const errors = validateAssignmentTestDraft(value);
  const [importing, setImporting] = React.useState(false);
  const [importMessage, setImportMessage] = React.useState("");

  const importWord = async (file?: File) => {
    if (!file) return;
    setImporting(true);
    setImportMessage("");
    try {
      const imported = await importAssignmentTestFromWord(file);
      onChange({
        questions: imported.questions.map((question) => createQuestion({
          ...question,
          options: question.options.map((option) => createOption(option)),
        })),
      });
      setImportMessage(`Загружено вопросов: ${imported.questions.length}. Проверьте правильные ответы перед сохранением.`);
    } catch (error: any) {
      setImportMessage(error?.response?.data?.detail || "Не удалось импортировать тест из Word.");
    } finally {
      setImporting(false);
    }
  };

  const toggleEnabled = (enabled: boolean) => {
    onChange(enabled ? { questions: [createQuestion()] } : null);
  };

  const updateQuestion = (index: number, question: AssignmentTestQuestion) => {
    if (!value) return;
    onChange({
      ...value,
      questions: value.questions.map((item, currentIndex) => currentIndex === index ? question : item),
    });
  };

  const handleQuestionDragEnd = (event: DragEndEvent) => {
    if (!value) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const questions = reorderAssignmentTestQuestions(value.questions, active.id, over.id);
    if (questions !== value.questions) onChange({ ...value, questions });
  };

  return (
    <BuilderSection>
      <BuilderHead>
        <div>
          <BuilderTitle>Тест внутри домашнего задания</BuilderTitle>
          <BuilderHint>Ответы проверяются автоматически, результат сохраняется вместе с версией ответа ученика.</BuilderHint>
        </div>
        <Switch
          checked={Boolean(value)}
          onChange={toggleEnabled}
          label="Добавить тест"
          ariaLabel="Добавить тест к домашнему заданию"
        />
      </BuilderHead>

      {value ? (
        <>
          <WordImportBox>
            <OptionsTitle>Загрузить готовый тест из Word</OptionsTitle>
            <BuilderHint>DOCX: вопросы нумеруются 1., 2.; варианты А., Б. или 1.; правильный ответ отмечается [x]. Комментарии после импорта остаются пустыми.</BuilderHint>
            <WordFileButton>
              {importing ? "Загружаем и проверяем..." : "Загрузить тест из Word"}
              <HiddenFileInput
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                disabled={importing}
                onChange={(event) => void importWord(event.target.files?.[0])}
              />
            </WordFileButton>
            {importMessage ? <ValidationMessage>{importMessage}</ValidationMessage> : null}
          </WordImportBox>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleQuestionDragEnd}>
            <SortableContext
              items={value.questions.map((question) => sortableId(question))}
              strategy={verticalListSortingStrategy}
            >
              <QuestionsList>
                {value.questions.map((question, index) => (
                  <SortableQuestion
                    key={sortableId(question)}
                    question={question}
                    index={index}
                    onChange={(nextQuestion) => updateQuestion(index, nextQuestion)}
                    onDuplicate={() => {
                      const duplicate = createQuestion({
                        question_type: question.question_type,
                        text: question.text,
                        comment: question.comment,
                        attachment: question.attachment || null,
                        attachment_file: question.attachment_file || null,
                        remove_attachment: false,
                        options: question.options.map((option) => createOption({
                          text: option.text,
                          is_correct: option.is_correct,
                        })),
                      });
                      onChange({
                        ...value,
                        questions: [
                          ...value.questions.slice(0, index + 1),
                          duplicate,
                          ...value.questions.slice(index + 1),
                        ],
                      });
                    }}
                    onRemove={() => onChange({
                      ...value,
                      questions: value.questions.filter((_, currentIndex) => currentIndex !== index),
                    })}
                  />
                ))}
              </QuestionsList>
            </SortableContext>
          </DndContext>
          <AddQuestionRow>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ ...value, questions: [...value.questions, createQuestion()] })}
            >
              Добавить вопрос
            </Button>
          </AddQuestionRow>
          {errors.length > 0 ? <ValidationMessage>{errors[0]}</ValidationMessage> : null}
        </>
      ) : null}
    </BuilderSection>
  );
};

export default AssignmentTestBuilder;
